import CloseIcon from "@mui/icons-material/Close";
import VolunteerActivism from "@mui/icons-material/VolunteerActivism";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { useId } from "react";
import { CAUSE_BENEFICIARY_MODAL } from "@/constants/campaings";
import { useCharityCauses } from "@/hooks/useCharityCauses";
import type { Campaign } from "@/interfaces/campaing";
import type { CharityCause } from "@/interfaces/charity";

export interface CauseBeneficiaryModalProps {
  open: boolean;
  onClose: () => void;
  campaign: Campaign;
}

/** Same fill GfgModal's primary button darkens to on hover. */
const PRIMARY_HOVER_BG = "#1a1a1a";
const HERO_HEIGHT = 240;

/** "Your Cause Beneficiary" for 0 or 1 cause, "Your Cause Beneficiaries" for 2+. */
export function causeBeneficiaryTitle(causeCount: number): string {
  return causeCount > 1
    ? CAUSE_BENEFICIARY_MODAL.TITLE_PLURAL
    : CAUSE_BENEFICIARY_MODAL.TITLE_SINGULAR;
}

/** Organisation names to show: the campaign's primary org first, then each cause's org from the
 *  catalogue; deduplicated; unknown ids skipped. */
export function resolveCauseOrgNames(
  primaryOrg: string,
  causeProductIds: string[],
  catalogue: Pick<CharityCause, "causeProductId" | "organization" | "title">[],
): string[] {
  const byProductId = new Map(catalogue.map((cause) => [cause.causeProductId, cause]));
  const candidates = [
    primaryOrg,
    ...causeProductIds.map((id) => {
      const cause = byProductId.get(id);
      return cause ? (cause.organization ?? cause.title) : "";
    }),
  ];

  const seen = new Set<string>();
  const names: string[] = [];
  for (const candidate of candidates) {
    const name = candidate.trim();
    if (name !== "" && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }

  return names;
}

/** "" | "A" | "A and B" | "A, B and C". */
export function formatOrgNames(names: string[]): string {
  const last = names.at(-1);
  if (last === undefined) return "";
  if (names.length === 1) return last;
  return `${names.slice(0, -1).join(", ")} and ${last}`;
}

export default function CauseBeneficiaryModal({
  open,
  onClose,
  campaign,
}: CauseBeneficiaryModalProps) {
  const { data } = useCharityCauses();
  const titleId = useId();
  // impactPledge fallback: legacy campaigns persisted before impactPledgeOrg existed (see
  // CampaignCard's inline summary, which uses the same fallback).
  const names = resolveCauseOrgNames(
    campaign.impactPledgeOrg || campaign.impactPledge,
    campaign.causeProductIds,
    data ?? [],
  );
  const orgsText = formatOrgNames(names);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          borderRadius: "var(--radius-card)",
          border: "1px solid var(--color-smoke)",
          width: 560,
          maxWidth: "calc(100vw - 32px)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <IconButton
          aria-label={CAUSE_BENEFICIARY_MODAL.CLOSE_LABEL}
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 40,
            height: 40,
            border: "1px solid var(--color-smoke)",
            borderRadius: "var(--radius-btn, 6px)",
            bgcolor: "white",
            color: "var(--color-rodeo-black)",
            "&:hover": { bgcolor: "var(--color-wax)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {campaign.impactPledgeImageUrl ? (
          <Box
            component="img"
            src={campaign.impactPledgeImageUrl}
            alt={names[0] ?? ""}
            sx={{
              width: "100%",
              height: HERO_HEIGHT,
              objectFit: "cover",
              borderRadius: "var(--radius-sm)",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: HERO_HEIGHT,
              borderRadius: "var(--radius-sm)",
              bgcolor: "var(--color-light-dust)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VolunteerActivism sx={{ fontSize: 64, color: "var(--color-dark-dust)" }} />
          </Box>
        )}

        <Typography
          id={titleId}
          sx={{
            fontFamily: "var(--font-crimson)",
            fontSize: 30,
            lineHeight: 1.2,
            color: "var(--color-rodeo-black)",
            textAlign: "center",
          }}
        >
          {causeBeneficiaryTitle(campaign.causeCount)}
        </Typography>

        <Typography
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: 16,
            color: "var(--color-graphite)",
            textAlign: "center",
          }}
        >
          {CAUSE_BENEFICIARY_MODAL.BODY_BEFORE_ORGS}
          <strong>{orgsText}</strong>
          {CAUSE_BENEFICIARY_MODAL.BODY_AFTER_ORGS}
        </Typography>

        <Button
          onClick={onClose}
          disableElevation
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.98px",
            textTransform: "uppercase",
            bgcolor: "var(--color-rodeo-black)",
            color: "white",
            borderRadius: "var(--radius-btn)",
            px: "20px",
            height: "34px",
            flexShrink: 0,
            "&:hover": { bgcolor: PRIMARY_HOVER_BG },
          }}
        >
          {CAUSE_BENEFICIARY_MODAL.CLOSE_LABEL}
        </Button>
      </Box>
    </Dialog>
  );
}
